// ============================================
// UTILITY FUNCTIONS
// ============================================

import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';
import { format, formatDistanceToNow } from 'date-fns';
import { tr } from 'date-fns/locale';

/**
 * Merge Tailwind CSS classes
 */
export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Format number with locale
 */
export function formatNumber(value: number): string {
  return new Intl.NumberFormat('tr-TR').format(value);
}

/**
 * Format currency
 */
export function formatCurrency(value: number, currency: string = 'USD'): string {
  return new Intl.NumberFormat('tr-TR', {
    style: 'currency',
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
}

/**
 * Format date
 */
export function formatDate(date: string | Date, formatStr: string = 'dd MMM yyyy'): string {
  return format(new Date(date), formatStr, { locale: tr });
}

/**
 * Format relative time
 */
export function formatRelativeTime(date: string | Date): string {
  return formatDistanceToNow(new Date(date), { addSuffix: true, locale: tr });
}

/**
 * Truncate text
 */
export function truncate(text: string, length: number): string {
  if (text.length <= length) return text;
  return text.slice(0, length) + '...';
}

/**
 * Get initials from name
 */
export function getInitials(firstName?: string, lastName?: string): string {
  if (!firstName && !lastName) return '??';
  
  // If only one parameter (full name)
  if (!lastName) {
    return firstName!
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  }
  
  // If two parameters (firstName, lastName)
  const firstInitial = firstName?.[0]?.toUpperCase() || '';
  const lastInitial = lastName?.[0]?.toUpperCase() || '';
  return `${firstInitial}${lastInitial}` || '??';
}

/**
 * Debounce function
 */
export function debounce<T extends (...args: unknown[]) => unknown>(
  fn: T,
  delay: number
): (...args: Parameters<T>) => void {
  let timeoutId: ReturnType<typeof setTimeout>;
  return (...args: Parameters<T>) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => fn(...args), delay);
  };
}

/**
 * Sleep utility
 */
export function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Status badge variant mapper
 */
export function getStatusVariant(status: string): 'success' | 'warning' | 'error' | 'info' | 'default' {
  const statusMap: Record<string, 'success' | 'warning' | 'error' | 'info' | 'default'> = {
    ACTIVE: 'success',
    APPROVED: 'success',
    ACCEPTED: 'success',
    HIRED: 'success',
    VERIFIED: 'success',
    PENDING: 'warning',
    PENDING_VERIFICATION: 'warning',
    IN_REVIEW: 'warning',
    SHORTLISTED: 'info',
    INTERVIEW: 'info',
    OFFERED: 'info',
    PAUSED: 'warning',
    DRAFT: 'default',
    INACTIVE: 'default',
    REJECTED: 'error',
    WITHDRAWN: 'error',
    SUSPENDED: 'error',
    CLOSED: 'default',
    EXPIRED: 'default',
  };
  return statusMap[status] || 'default';
}

/**
 * Application status labels (Turkish)
 */
export const applicationStatusLabels: Record<string, string> = {
  PENDING: 'Beklemede',
  REVIEWED: 'İncelendi',
  SHORTLISTED: 'Ön Eleme',
  INTERVIEW: 'Mülakat',
  OFFERED: 'Teklif Yapıldı',
  ACCEPTED: 'Kabul Edildi',
  REJECTED: 'Reddedildi',
  WITHDRAWN: 'Geri Çekildi',
};

/**
 * Job status labels (Turkish)
 */
export const jobStatusLabels: Record<string, string> = {
  DRAFT: 'Taslak',
  ACTIVE: 'Aktif',
  PAUSED: 'Duraklatıldı',
  CLOSED: 'Kapatıldı',
  EXPIRED: 'Süresi Doldu',
};

/**
 * Department labels (Turkish)
 */
export const departmentLabels: Record<string, string> = {
  DECK: 'Güverte',
  ENGINE: 'Makine',
  ELECTRICAL: 'Elektrik',
  CATERING: 'İkram/Kamarote',
  SAFETY: 'Emniyet',
  MEDICAL: 'Sağlık',
  RADIO: 'Haberleşme/Telsiz',
  CREWING: 'Mürettebat/İK',
  FINANCE: 'Finans/Muhasebe',
  SALES: 'Satış/Pazarlama',
  IT: 'Bilgi Teknolojileri',
  LOGISTICS: 'Lojistik/Operasyon',
  MANAGEMENT: 'Yönetim/İdari',
  OFFSHORE: 'Açık Deniz',
  OTHER: 'Diğer',
};

/**
 * Vessel type labels (Turkish)
 */
export const vesselTypeLabels: Record<string, string> = {
  BULK_CARRIER: 'Dökme Yük',
  TANKER: 'Tanker',
  CONTAINER: 'Konteyner',
  GENERAL_CARGO: 'Genel Kargo',
  RORO: 'Ro-Ro',
  PASSENGER: 'Yolcu',
  OFFSHORE: 'Offshore',
  TUG: 'Römorkör',
  YACHT: 'Yat',
  FISHING: 'Balıkçı',
  OTHER: 'Diğer',
};

/**
 * Country list for maritime industry
 * Sorted alphabetically by Turkish name
 */
export const countries = [
  { code: 'VI', name: 'ABD Virjin Adaları' },
  { code: 'AF', name: 'Afganistan' },
  { code: 'DE', name: 'Almanya' },
  { code: 'US', name: 'Amerika Birleşik Devletleri' },
  { code: 'AD', name: 'Andorra' },
  { code: 'AO', name: 'Angola' },
  { code: 'AG', name: 'Antigua ve Barbuda' },
  { code: 'AR', name: 'Arjantin' },
  { code: 'AL', name: 'Arnavutluk' },
  { code: 'AU', name: 'Avustralya' },
  { code: 'AT', name: 'Avusturya' },
  { code: 'AZ', name: 'Azerbaycan' },
  { code: 'BS', name: 'Bahamalar' },
  { code: 'BH', name: 'Bahreyn' },
  { code: 'BB', name: 'Barbados' },
  { code: 'BD', name: 'Bangladeş' },
  { code: 'BY', name: 'Belarus' },
  { code: 'BE', name: 'Belçika' },
  { code: 'BZ', name: 'Belize' },
  { code: 'BJ', name: 'Benin' },
  { code: 'BM', name: 'Bermuda' },
  { code: 'AE', name: 'Birleşik Arap Emirlikleri' },
  { code: 'GB', name: 'Birleşik Krallık' },
  { code: 'BO', name: 'Bolivya' },
  { code: 'BA', name: 'Bosna Hersek' },
  { code: 'BR', name: 'Brezilya' },
  { code: 'VG', name: 'Britanya Virjin Adaları' },
  { code: 'BN', name: 'Brunei' },
  { code: 'BG', name: 'Bulgaristan' },
  { code: 'BT', name: 'Butan' },
  { code: 'CV', name: 'Cabo Verde' },
  { code: 'KY', name: 'Cayman Adaları' },
  { code: 'GI', name: 'Cebelitarık' },
  { code: 'DZ', name: 'Cezayir' },
  { code: 'CI', name: "Côte d'Ivoire" },
  { code: 'CW', name: 'Curaçao' },
  { code: 'CZ', name: 'Çekya' },
  { code: 'CN', name: 'Çin' },
  { code: 'MO', name: 'Çin Makao ÖİB' },
  { code: 'DK', name: 'Danimarka' },
  { code: 'DO', name: 'Dominik Cumhuriyeti' },
  { code: 'DM', name: 'Dominika' },
  { code: 'EC', name: 'Ekvador' },
  { code: 'GQ', name: 'Ekvator Ginesi' },
  { code: 'SV', name: 'El Salvador' },
  { code: 'ID', name: 'Endonezya' },
  { code: 'ER', name: 'Eritre' },
  { code: 'AM', name: 'Ermenistan' },
  { code: 'EE', name: 'Estonya' },
  { code: 'ET', name: 'Etiyopya' },
  { code: 'MA', name: 'Fas' },
  { code: 'FO', name: 'Faroe Adaları' },
  { code: 'FJ', name: 'Fiji' },
  { code: 'PH', name: 'Filipinler' },
  { code: 'FI', name: 'Finlandiya' },
  { code: 'FR', name: 'Fransa' },
  { code: 'PF', name: 'Fransız Polinezyası' },
  { code: 'GA', name: 'Gabon' },
  { code: 'GM', name: 'Gambiya' },
  { code: 'GH', name: 'Gana' },
  { code: 'GN', name: 'Gine' },
  { code: 'GD', name: 'Grenada' },
  { code: 'GT', name: 'Guatemala' },
  { code: 'GY', name: 'Guyana' },
  { code: 'GE', name: 'Gürcistan' },
  { code: 'ZA', name: 'Güney Afrika' },
  { code: 'KR', name: 'Güney Kore' },
  { code: 'HT', name: 'Haiti' },
  { code: 'IN', name: 'Hindistan' },
  { code: 'HR', name: 'Hırvatistan' },
  { code: 'NL', name: 'Hollanda' },
  { code: 'HK', name: 'Hong Kong' },
  { code: 'HN', name: 'Honduras' },
  { code: 'IQ', name: 'Irak' },
  { code: 'IR', name: 'İran' },
  { code: 'IE', name: 'İrlanda' },
  { code: 'ES', name: 'İspanya' },
  { code: 'IL', name: 'İsrail' },
  { code: 'SE', name: 'İsveç' },
  { code: 'CH', name: 'İsviçre' },
  { code: 'IT', name: 'İtalya' },
  { code: 'IS', name: 'İzlanda' },
  { code: 'JM', name: 'Jamaika' },
  { code: 'JP', name: 'Japonya' },
  { code: 'KH', name: 'Kamboçya' },
  { code: 'CM', name: 'Kamerun' },
  { code: 'CA', name: 'Kanada' },
  { code: 'ME', name: 'Karadağ' },
  { code: 'QA', name: 'Katar' },
  { code: 'KZ', name: 'Kazakistan' },
  { code: 'KE', name: 'Kenya' },
  { code: 'CY', name: 'Kıbrıs' },
  { code: 'KI', name: 'Kiribati' },
  { code: 'CO', name: 'Kolombiya' },
  { code: 'KM', name: 'Komorlar' },
  { code: 'XK', name: 'Kosova' },
  { code: 'CR', name: 'Kosta Rika' },
  { code: 'CU', name: 'Küba' },
  { code: 'KW', name: 'Kuveyt' },
  { code: 'MK', name: 'Kuzey Makedonya' },
  { code: 'LA', name: 'Laos' },
  { code: 'LV', name: 'Letonya' },
  { code: 'LR', name: 'Liberya' },
  { code: 'LI', name: 'Liechtenstein' },
  { code: 'LY', name: 'Libya' },
  { code: 'LT', name: 'Litvanya' },
  { code: 'LB', name: 'Lübnan' },
  { code: 'LU', name: 'Lüksemburg' },
  { code: 'HU', name: 'Macaristan' },
  { code: 'MG', name: 'Madagaskar' },
  { code: 'MW', name: 'Malavi' },
  { code: 'MY', name: 'Malezya' },
  { code: 'MV', name: 'Maldivler' },
  { code: 'ML', name: 'Mali' },
  { code: 'MT', name: 'Malta' },
  { code: 'IM', name: 'Man Adası' },
  { code: 'MH', name: 'Marshall Adaları' },
  { code: 'MU', name: 'Mauritius' },
  { code: 'MX', name: 'Meksika' },
  { code: 'EG', name: 'Mısır' },
  { code: 'FM', name: 'Mikronezya' },
  { code: 'MD', name: 'Moldova' },
  { code: 'MC', name: 'Monako' },
  { code: 'MR', name: 'Moritanya' },
  { code: 'MZ', name: 'Mozambik' },
  { code: 'MN', name: 'Moğolistan' },
  { code: 'MM', name: 'Myanmar' },
  { code: 'NA', name: 'Namibya' },
  { code: 'NR', name: 'Nauru' },
  { code: 'NP', name: 'Nepal' },
  { code: 'NI', name: 'Nikaragua' },
  { code: 'NG', name: 'Nijerya' },
  { code: 'NO', name: 'Norveç' },
  { code: 'OM', name: 'Umman' },
  { code: 'UZ', name: 'Özbekistan' },
  { code: 'PW', name: 'Palau' },
  { code: 'PK', name: 'Pakistan' },
  { code: 'PA', name: 'Panama' },
  { code: 'PG', name: 'Papua Yeni Gine' },
  { code: 'PY', name: 'Paraguay' },
  { code: 'PE', name: 'Peru' },
  { code: 'PL', name: 'Polonya' },
  { code: 'PT', name: 'Portekiz' },
  { code: 'PR', name: 'Porto Riko' },
  { code: 'RO', name: 'Romanya' },
  { code: 'RU', name: 'Rusya' },
  { code: 'KN', name: 'Saint Kitts ve Nevis' },
  { code: 'LC', name: 'Saint Lucia' },
  { code: 'VC', name: 'Saint Vincent ve Grenadinler' },
  { code: 'WS', name: 'Samoa' },
  { code: 'SM', name: 'San Marino' },
  { code: 'ST', name: 'Sao Tome ve Principe' },
  { code: 'SA', name: 'Suudi Arabistan' },
  { code: 'SN', name: 'Senegal' },
  { code: 'SC', name: 'Seyşeller' },
  { code: 'SL', name: 'Sierra Leone' },
  { code: 'SG', name: 'Singapur' },
  { code: 'SK', name: 'Slovakya' },
  { code: 'SI', name: 'Slovenya' },
  { code: 'SB', name: 'Solomon Adaları' },
  { code: 'CL', name: 'Şili' },
  { code: 'SO', name: 'Somali' },
  { code: 'SD', name: 'Sudan' },
  { code: 'SR', name: 'Surinam' },
  { code: 'SY', name: 'Suriye' },
  { code: 'RS', name: 'Sırbistan' },
  { code: 'LK', name: 'Sri Lanka' },
  { code: 'TJ', name: 'Tacikistan' },
  { code: 'TZ', name: 'Tanzanya' },
  { code: 'TH', name: 'Tayland' },
  { code: 'TW', name: 'Tayvan' },
  { code: 'TL', name: 'Timor-Leste' },
  { code: 'TG', name: 'Togo' },
  { code: 'TO', name: 'Tonga' },
  { code: 'TT', name: 'Trinidad ve Tobago' },
  { code: 'TN', name: 'Tunus' },
  { code: 'TC', name: 'Turks ve Caicos Adaları' },
  { code: 'TR', name: 'Türkiye' },
  { code: 'TM', name: 'Türkmenistan' },
  { code: 'TV', name: 'Tuvalu' },
  { code: 'UA', name: 'Ukrayna' },
  { code: 'UG', name: 'Uganda' },
  { code: 'UY', name: 'Uruguay' },
  { code: 'JO', name: 'Ürdün' },
  { code: 'VU', name: 'Vanuatu' },
  { code: 'VE', name: 'Venezuela' },
  { code: 'VN', name: 'Vietnam' },
  { code: 'YE', name: 'Yemen' },
  { code: 'NZ', name: 'Yeni Zelanda' },
  { code: 'GR', name: 'Yunanistan' },
  { code: 'ZM', name: 'Zambiya' },
  { code: 'ZW', name: 'Zimbabve' },
] as const;

/**
 * Get country name by code
 */
export function getCountryName(code: string): string {
  const country = countries.find(c => c.code === code);
  return country?.name || code;
}
